import template from './mx-theme-preview.component.html';
import { equals, copy, IAugmentedJQuery, IController, IScope } from 'angular';
import type { MxContainerControllerInterface } from '../../Shell/PandoraComponents/mx-container/mx-container';
import type { ThemeService } from '../mx-theme.service';
import { ThemeParameter, ThemeVariable } from '../theme.contracts';
import { EntityService, EnumerationValue } from '../../Shell/PandoraSolutionBuilderAgent/services/entity-service';

export type ThemeParameters = Record<string, string | number | boolean | null>;

const ICON_SET_VAR_NAME = 'IconSet';

export class MxThemePreviewController implements IController {
	static readonly $inject = ['$scope', '$element', 'mx.app.ThemeService', 'mx.SolutionBuilderAgent.EntityService'];

	/* properties that are not theme determinative */
	exceptions = ['Sys-TimeStamp', 'ID', 'Name', 'Description', 'CIValues', 'IsActive', 'IsAvailable', 'IsActiveForNewUi'];
	/* properties that are not styles */
	exclusions = [...this.exceptions, 'IsContrast'];

	loaded = false;
	private mxContainerCtrl?: MxContainerControllerInterface;
	private autoUpdate?: string;
	private iframe!: HTMLIFrameElement;
	private iconPaths?: Map<number, string>;

	previewSrc?: string;

	constructor(
		private readonly $scope: IScope,
		private readonly $element: IAugmentedJQuery,
		private readonly themeService: ThemeService,
		private entityService: EntityService
	) {}

	$postLink() {
		this.iframe = this.$element.find('iframe')[0];
		this.iframe.onload = () => {
			this.initWatcher();
		};
		this.iframe.src = `${this.previewSrc}?ver=0.${Date.now()}`;
	}

	$onInit() {
		if (this.mxContainerCtrl) {
			this.mxContainerCtrl.registerChild(this.$scope);
		}
		if (!this.previewSrc) {
			this.previewSrc = 'themePreview/themePreview.html';
		}
	}

	private async loadIconPaths() {
		if (!this.iconPaths) {
			const items = (await this.entityService.getEnumerationValues('DWPThemeIconSetPickup')) as Array<EnumerationValue & { Path: string }>;
			this.iconPaths = new Map(items.map((item) => [item.Value, item.Path]));
		}

		return this.iconPaths;
	}

	private async getIconPath(id: number) {
		return (await this.loadIconPaths()).get(id) || null;
	}

	private toThemeParamName(value: string): ThemeVariable {
		if (value === 'Logo') {
			return 'applicationLogoUrl';
		} else if (value === ICON_SET_VAR_NAME) {
			return 'icons-font-path';
		}
		return value.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`).substring(1) as ThemeVariable;
	}

	private async applyVariables(parameters: ThemeParameters) {
		const isContrast = parameters['IsContrast'];

		parameters = copy(parameters);
		this.exclusions.forEach((exclusion) => {
			delete parameters[exclusion];
		});

		if (parameters[ICON_SET_VAR_NAME]) {
			parameters[ICON_SET_VAR_NAME] = await this.getIconPath(parameters[ICON_SET_VAR_NAME] as number);
		}

		const themeParameters = Object.entries(parameters).map(([key, value]) => {
			const themeKey = this.toThemeParamName(key);

			if (themeKey === 'applicationLogoUrl' && typeof value === 'string' && !value.includes('data:image')) {
				value = `/m42services/${value}`;
			}

			return { Key: themeKey, Value: value } as ThemeParameter;
		});

		const { isValid } = await this.themeService.validateTheme(themeParameters);
		if (!isValid) {
			return;
		}

		let theme = this.themeService.getThemeFromParameters(themeParameters);
		theme = isContrast ? this.themeService.calculateContrastTheme(theme) : this.themeService.calculateStandardTheme(theme);

		const doc = this.iframe.contentDocument as Document;
		this.themeService.applyCssVariables('preview', theme, doc);

		let styleTag = doc.querySelector('[id^="mx-theme-variables--"]') as HTMLStyleElement;
		if (!styleTag) {
			styleTag = doc.createElement('style');
			doc.body.appendChild(styleTag);
		}

		styleTag.appendChild(doc.createTextNode('*{transition:none !important;}')); // disable most animations to ensure smoother refresh on change
		styleTag.appendChild(doc.createTextNode('body {zoom:0.7;} @media screen and (min-width: 1500px) {body {zoom: 1;}}')); // zoom preview
	}

	private initWatcher() {
		this.$scope.$watch(
			'vm.theme',
			$.debounce(this.loaded ? 1000 : 0, (newValue: ThemeParameters, oldValue: ThemeParameters) => {
				this.$scope.$evalAsync(async () => {
					if (this.autoUpdate !== 'true' || (this.loaded && this.themesEqual(newValue, oldValue))) {
						return;
					}

					if (this.iframe) {
						const width = Math.max(this.$element.find('#frame-wrap').width() || 0, 960);

						this.iframe.style.width = `${width}px`;
						this.iframe.style.height = `${width * (1080 / 1920)}px`;
						this.loaded = true;

						await this.applyVariables(newValue);

						$(window).resize(); // Force redrawing tabs near this Theme preview
					}
				});
			}),
			true
		);
	}

	private themesEqual(newValue: ThemeParameters, oldValue: ThemeParameters) {
		const themeNew = copy(newValue);
		const themeOld = copy(oldValue);

		this.exceptions.forEach((exception) => {
			delete themeNew[exception];
			delete themeOld[exception];
		});

		return equals(themeNew, themeOld);
	}
}

export const MxThemePreviewComponent = {
	template: template,
	controller: MxThemePreviewController,
	controllerAs: 'vm',
	bindings: {
		theme: '=?',
		autoUpdate: '@',
		previewSrc: '@',
	},
	require: {
		mxContainerCtrl: '?^^mxContainer',
	},
};
